import { useCallback, useEffect, useRef, useState } from 'react'

import { impressionStatusColor } from '../constants'
import { ImpressionStatus, ImpressionTracker, TrackerOption } from '../interfaces'
import { addColoredLayer, removeLayer } from '../utils'

const useImpressionTracker = <E extends HTMLElement = HTMLElement, Context = unknown>({
  isImpressionVisible = false,
  onImpressed,
  context,
  root = null,
  rootMargin = '0px',
  visibilityThreshold = 0.5,
  durationThreshold = 1000,
  hasImpressed = false,
}: TrackerOption<Context>): ImpressionTracker<E> => {
  const trackerInfoRef = useRef<{
    timeoutId: number | null
    observer: IntersectionObserver | null
  }>({ timeoutId: null, observer: null })
  const targetRef = useRef<E>(null)
  const [impressionStatus, setImpressionStatus] = useState<ImpressionStatus>(ImpressionStatus.NONE)

  const changeStatusNone = useCallback(() => {
    setImpressionStatus(ImpressionStatus.NONE)
  }, [])

  const changeStatusImpressed = useCallback(() => {
    if (!targetRef.current || !trackerInfoRef.current.observer) {
      return
    }

    setImpressionStatus(ImpressionStatus.IMPRESSED)
    trackerInfoRef.current.timeoutId = null
    trackerInfoRef.current.observer.unobserve(targetRef.current)

    onImpressed({ context })
  }, [context, onImpressed])

  const changeStatusOnImpressing = useCallback(() => {
    setImpressionStatus(ImpressionStatus.OVER_VISUAL_THRESHOLD)

    trackerInfoRef.current.timeoutId = setTimeout(() => {
      changeStatusImpressed()
    }, durationThreshold) as unknown as number
  }, [changeStatusImpressed, durationThreshold])

  const changeStatusNotOnImpressing = useCallback(() => {
    clearTimeout(trackerInfoRef.current.timeoutId as number)
    setImpressionStatus(ImpressionStatus.UNDER_VISUAL_THRESHOLD)
    trackerInfoRef.current.timeoutId = null
  }, [])

  if (typeof window === 'object') {
    trackerInfoRef.current.observer ??= new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          const { intersectionRatio } = entry

          if (impressionStatus === ImpressionStatus.IMPRESSED || !entry.isIntersecting) {
            return
          }

          if (intersectionRatio >= visibilityThreshold) {
            if (trackerInfoRef.current.timeoutId) {
              return
            }

            changeStatusOnImpressing()
            return
          }

          changeStatusNotOnImpressing()
        })
      },
      {
        root,
        rootMargin,
        threshold: [...Array<number>(1000)].map((_, index) => index / 1000),
      },
    )
  }

  const startTracking = useCallback(() => {
    if (!targetRef.current || !trackerInfoRef.current.observer || hasImpressed) {
      return
    }

    changeStatusNotOnImpressing()
    trackerInfoRef.current.observer.observe(targetRef.current)
  }, [changeStatusNotOnImpressing, hasImpressed])

  const stopTracking = useCallback(() => {
    if (!targetRef.current || !trackerInfoRef.current.observer) {
      return
    }

    changeStatusNone()
    trackerInfoRef.current.observer.unobserve(targetRef.current)
  }, [changeStatusNone])

  useEffect(() => {
    const target = targetRef.current

    if (!target) {
      return
    }

    if (!isImpressionVisible) {
      removeLayer(target)
      return
    }

    if (hasImpressed) {
      addColoredLayer(target, impressionStatusColor.IMPRESSED)
      return
    }

    switch (impressionStatus) {
      case ImpressionStatus.NONE:
        removeLayer(target)
        break
      case ImpressionStatus.UNDER_VISUAL_THRESHOLD:
        addColoredLayer(target, impressionStatusColor.UNDER_VISUAL_THRESHOLD)
        break
      case ImpressionStatus.OVER_VISUAL_THRESHOLD:
        addColoredLayer(target, impressionStatusColor.OVER_VISUAL_THRESHOLD)
        break
      case ImpressionStatus.IMPRESSED:
        addColoredLayer(target, impressionStatusColor.IMPRESSED)
        break
    }
  }, [hasImpressed, isImpressionVisible, impressionStatus])

  return {
    targetRef,

    startTracking,

    stopTracking,
  }
}

export default useImpressionTracker
